import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { Button, IconButton, Dialog, DialogContent } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import './MCTQuoteCard.scss';

const TooltipColor = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
    ribbonColor: {
        color: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}26`,
    },
    quoteCardColor: {
        color: theme.palette.primary.main,
    },
    priceColor: {
        color: theme.palette.primary.main,
        fontSize: "30px",
        fontWeight: "bold",
        letterSpacing: "-0.16px",
        '&::before': {
            color: theme.palette.primary.main,
            content: '"$"',
            fontSize: "12px",
            verticalAlign: "super",
        }
    },
    percentColor :{
        color: theme.palette.primary.main,
        fontSize: "29px",
        fontWeight: "bold",
        letterSpacing: "-0.16px",
        '&::before': {
            color: theme.palette.primary.main,
            fontSize: "12px",
            verticalAlign: "super",
        }
    },
    replacementLabel:{
        fontSize: '12px',
        fontWeight: '700',
        lineHeight: '0.875rem',
        margin: '0 0 .35rem 0'
    },
    priceText: {
        color: theme.palette.primary.main,
        fontSize: "12px"
    },
    lightBackground: {
        backgroundColor: `${theme.palette.primary.main}05`,
    },
}));

export default function DisabilityInsuranceCard(props) {
    const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);
    const classes = useStyles();

    const getBadge = () => {
        var bestCount = [props.requiresMedicalExam].filter((f) => f === true).length;

        // now either it only has 1 badge, or it doesn't have the badge being sorted by, so pick one

        if (bestCount && props.requiresMedicalExam) {
            return (
                <Fragment>
                    Medical Exam Requires
                </Fragment>
            );
        }
    };

    const productDetailsButton = (productName, isMobile) => {
        return (
            <Row>
                <Col>
                    <Button className={`small-text ${isMobile ? "mobile-details-button" : ""}`} color="primary" onClick={() => setIsProductDetailsDialogOpen(true)} aria-describedby={productName}>{!isMobile && `PRODUCT`} DETAILS</Button>
                </Col>
            </Row>
        );
    }

    return (
        <div className="mct-card-wrapper">
            {props.requiresMedicalExam && <div className={`ribbon ribbon-text ${classes.ribbonColor}`}>{getBadge()}</div>}
            <Card variant="outlined" className="highlight-quote-card">
                <CardContent className="card-content">
                    <Row className="align-items-center">
                        <Col lg={3} className={`carrier-branding-container d-flex flex-column justify-content-center align-items-center ${classes.lightBackground}`}>
                            <img className="carrier-logo-lg" src={props.logoUrl} alt="Breeze Icon" />
                            <div className='product-buttoncard'>{productDetailsButton(props.productName)}</div>
                        </Col>
                        <Col lg={4} className="stat-container flex-column justify-content-center">
                            <Stats
                                benefitAmount={props.benefitAmount}
                                incomeReplacement={props.incomeReplacement}
                                incomeReplacementLabel={props.incomeReplacementLabel}
                            />
                        </Col>
                        <Col lg={5} className="text-left pl-0 card-item-group">
                            <Row>
                                <Col lg={6} className="pl-0 d-flex flex-column justify-content-center">
                                    <p className="price-container">
                                        <NumberFormat
                                            className={`${classes.priceColor}`}
                                            value={props.monthlyRate}
                                            displayType={'text'}
                                            thousandSeparator={true}
                                            decimalScale={2}
                                        />
                                        <span className={` mt-2 quote-amount-mo ${classes.priceText}`}> /mo</span>
                                    </p>
                                    <p className={props.open ? 'hidden-title' : `${classes.replacementLabel} mt-2 stat-title month-cost color-startitle text-center`}>Monthly Cost</p>
                                </Col>
                                <Col lg={6} className="d-flex flex-column justify-content-center pl-0 select-container">
                                    <Button className="px-4" color="primary" variant="contained" onClick={props.onClick} aria-describedby={props.productName}>
                                        Select
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </CardContent>
            </Card>

            <ProductDetailsDialog
                isOpen={isProductDetailsDialogOpen}
                productDetails={props.productDetails}
                quoteCardProps={props}
                onClose={() => setIsProductDetailsDialogOpen(false)}
            />
        </div >
    );
}

export const ProductDetailsDialog = (props) => {

    const [tooltip, setTooltip] = useState(false);

    const handleTooltipClose = () => {
        setTooltip(false);
    };

    const handleTooltipOpen = () => {
        setTooltip(true);
    };
    const classes = useStyles();

    return (
        <Dialog className="product-details-dialog" open={props.isOpen} scroll="body" fullWidth={true} maxWidth="md" aria-labelledby="product-details-dialog">
            <DialogContent className="product-details-dialog-content">
                <IconButton aria-label="close" onClick={props.onClose} className="close-button">
                    <CloseIcon />
                </IconButton>
                <img className="carrier-logo-lg" src={props.quoteCardProps.logoUrl} alt="Breeze Icon" />

                <p className={`mt-3 textRating ${classes.quoteCardColor}`}>
                    {props.quoteCardProps.rating}
                    <span className="ml-1">Rating</span>
                </p>

                <div className="product mb-1">
                    <span><h5>{props.quoteCardProps.productName}</h5></span>
                </div>

                <Row>
                    <Col xs="3" md="5">
                        <Stats
                            benefitAmount={props.quoteCardProps.benefitAmount}
                            incomeReplacement={props.quoteCardProps.incomeReplacement}
                            monthlyRate={props.quoteCardProps.monthlyRate}
                            open={props.isOpen}
                        />
                    </Col>
                    <Col md='4'></Col>
                    <Col xs="6" md="3">
                        {!props.hideSelect && <Button className={`w-100 apply ${props.quoteCardProps.hideSelect ? "d-none" : ""}`} color="primary" variant="contained" onClick={props.quoteCardProps.onClick}>
                            Select
                        </Button>}
                    </Col>
                </Row>

                <Row className='ml-2'>
                    <div >
                        <div>
                            <b>Approval Time</b><br />
                            <span className='option-info'>TBD</span>
                        </div>
                    </div>
                    <div>
                        <div className='ml-3'>
                            <b>Medical Exam</b><br />
                            <span className='option-info'>In some cases</span>
                        </div>
                    </div>
                </Row>

                <div className="product-details-info">{props.productDetails}</div>

                <div className="additional-options mt-4">
                    <strong>
                        <h5><b>Additional Options</b></h5>
                    </strong>
                    <Row>
                        {props.quoteCardProps.hasPermConversion && (
                            <Col className="mt-2 ml-2">
                                <ul className="grey-text" dangerouslySetInnerHTML={{ __html: props.quoteCardProps.convertibility }}></ul>
                            </Col>
                        )}
                    </Row>
                    {(props.quoteCardProps.availableRiders && props.quoteCardProps.availableRiders.length > 0) && (
                        <strong className="mt-4 ml-2">
                            <b>Available Policy Riders</b>
                            <Tooltip className="toolInfo ml-2" title="Riders add benefits to or change the term of an insurance policy.">
                                <TooltipColor role="tooltip" onClose={handleTooltipClose} open={tooltip} leaveTouchDelay={5000}>
                                    <button id="riders" role="tooltip" aria-label="more about riders" className="quote-tooltip" onMouseOver={handleTooltipOpen} onClick={handleTooltipOpen} tabIndex="0" aria-disabled="true" aria-describedby="riders">
                                        <InfoIcon color="action"></InfoIcon>
                                    </button>
                                </TooltipColor>
                            </Tooltip>
                        </strong>
                    )}

                    {props.quoteCardProps.availableRiders &&
                        props.quoteCardProps.availableRiders.length > 0 &&
                        props.quoteCardProps.availableRiders.map((r, index) => {
                            return (
                                <Row key={index}>
                                    <Col md="3">
                                        <p className="option-title font-option-title mt-2 ml-3">{r.rider}</p>
                                        {r.additionalCost && (
                                            <p className="price-extra mt-2 ml-3">
                                                <MonetizationOnOutlinedIcon /> <b className="addtional">Additional Cost</b>
                                            </p>
                                        )}
                                        {!r.additionalCost && (
                                            <p className="mt-2 ml-3 moneyIcon">
                                                <MonetizationOnOutlinedIcon /> <b className={`noAddtional noAddtionalText ${classes.quoteCardColor}`}>No Additional Cost</b>
                                            </p>
                                        )}
                                    </Col>
                                    <Col>
                                        <p dangerouslySetInnerHTML={{ __html: r.description }} className="grey-text mt-1 ml-2"></p>
                                    </Col>
                                </Row>
                            );
                        })}
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Stats = (props) => {
    const classes = useStyles();
    return (
        <div className="product-details-stats">
            <Row>
                <Col lg={6} className="stat-container flex-column justify-content-center">
                    <p className="d-flex justify-content-center">
                        <NumberFormat
                            className={`${classes.percentColor}`}
                            value={props.incomeReplacement}
                            displayType={'text'}
                            thousandSeparator={true}
                            decimalScale={2}
                            mask={'%'}
                        />
                        <span className={`quote-amount-mo ${classes.priceText}`}> %</span>
                    </p>
                    <p className={`${classes.replacementLabel} text-center color-startitle`}>{props.incomeReplacementLabel}</p>
                </Col>
                <Col lg={6} className='d-flex flex-column justify-content-center'>
                    <p className="d-flex justify-content-center">
                        <NumberFormat
                            className={`${classes.priceColor}`}
                            value={props.benefitAmount}
                            displayType={'text'}
                            thousandSeparator={true}
                            decimalScale={2}
                        />
                        <span className={`quote-amount-mo mt-2 ${classes.priceText}`}> /mo</span>
                    </p>
                    <p className={props.open ? 'hidden-title' : "stat-title color-startitle text-center"}>Benefits</p>
                </Col>
            </Row>
        </div>
    );
};

DisabilityInsuranceCard.propTypes = {
    carrierName: PropTypes.string.isRequired,
    monthlyRate: PropTypes.number.isRequired,
    onClick: PropTypes.func.isRequired,
    productDetails: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/Cards/DisabilityInsuranceCard.js